import { accountStorageKey } from './accountSessions';
import { parseOperatorDeskScope, type OperatorDeskScope } from '../../shared/operatorDeskScope';
import { bidBoardAuthHeaders } from './bidBoardAuth';
import { operatorDeskBrandKey, type OperatorDeskWorkItem } from '../../shared/operatorDeskWork';
import type { OperatorProjectLocation } from '../../shared/operatorProjectName';

export async function checkOperatorProjectName(name: string, location: OperatorProjectLocation): Promise<void> {
  const response = await fetch('/api/operator-desk/project-name', {
    method: 'POST', headers: bidBoardAuthHeaders(), body: JSON.stringify({ name, ...location }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success) throw new Error(result.error || 'Could not check this project name. Please retry.');
}

export async function checkOperatorProjectNameAvailability(name: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch('/api/operator-desk/project-name', {
    method: 'POST', headers: bidBoardAuthHeaders(), body: JSON.stringify({ name, nameOnly: true }), signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success) {
    const err = String(result.error || '');
    // Soft-pass: Kind only needs a nonempty name. Seat paywall / budgets belong at Clock/Post.
    if (response.status === 402 || /^payment_required$/i.test(err) || /\bpayment_required\b/i.test(err)) return;
    throw new Error(err || 'Could not check this project name. Please retry.');
  }
}

export type { OperatorDeskWorkItem, OperatorDeskWorkUpdate, OperatorDeskWorkStatus } from '../../shared/operatorDeskWork';
export {
  OPERATOR_DESK_STATUS_COLORS,
  OPERATOR_DESK_STATUS_LABELS,
  operatorDeskWorkStatus,
  operatorDeskWorkStatusColor,
  operatorDeskApiDigits,
  operatorDeskBrandKey,
  groupOperatorDeskBrands,
} from '../../shared/operatorDeskWork';

export type OperatorDeskWorkListResult = {
  items: OperatorDeskWorkItem[];
  scope: OperatorDeskScope | null;
  /** True when the server likely has more rows beyond this capped fetch. */
  hasMore: boolean;
};

/**
 * Load Operator Desk work.
 *
 * Default is a single capped request (no `page` param) so the API returns at
 * most `limit` rows. Historically this client auto-paged with `page=N` until
 * exhausted — that multiplied payload (paged mode can return up to ~6×limit
 * per hop) and hammered the API every ~30s poll. Pass `fetchAll: true` only
 * when a one-shot caller truly needs the full mine universe.
 */
export async function listOperatorDeskWorkResult(input: {
  operator?: string | null;
  api?: string | null;
  limit?: number;
  mine?: boolean;
  history?: boolean;
  /** Private Projects/History: include closed and archived records, not just map-visible work. */
  workspace?: boolean;
  kind?: string | null;
  open?: boolean;
  /** Walk `page` until exhausted. Default false (one capped request). */
  fetchAll?: boolean;
  /** Cap auto-page depth when fetchAll is true. Ignored when fetchAll is false. */
  maxPages?: number;
}): Promise<OperatorDeskWorkListResult> {
  const params = new URLSearchParams();
  if (input.operator) params.set('operator', operatorDeskBrandKey(input.operator) || input.operator);
  if (input.api) params.set('api', input.api);
  if (input.limit) params.set('limit', String(input.limit));
  if (input.history) params.set('history', '1');
  if (input.workspace) params.set('workspace', '1');
  if (input.mine) { params.set('mine', '1'); params.set('company', '1'); }
  if (input.kind) params.set('kind', input.kind);
  if (input.open) params.set('open', '1');

  const fetchAll = Boolean(input.fetchAll);
  const maxPages = Math.max(1, Number(input.maxPages) || 20);
  const items = new Map<string, OperatorDeskWorkItem>();
  const account = accountStorageKey('project-read');
  let scope: OperatorDeskScope | null = null;
  let hasMore = false;
  let page = 1;

  while (true) {
    const pageParams = new URLSearchParams(params);
    // Omit `page` on the default single-hop path so the API slices to `limit`
    // total. Setting page activates per-source offsets that can return many
    // more rows in one response.
    if (fetchAll) pageParams.set('page', String(page));
    const res = await fetch(`/api/operator-desk/work?${pageParams.toString()}`, {
      headers: bidBoardAuthHeaders(),
    });
    const payload = await res.json().catch(() => ({}));
    if (account !== accountStorageKey('project-read')) throw new Error('Account changed. Please reload work.');
    if (!res.ok || payload.success === false) {
      throw Object.assign(new Error(res.status === 401
        ? 'Your sign-in could not be verified. Sign in again to load your work.'
        : payload.error || `Work list failed (${res.status})`), { status: res.status });
    }
    if (!Array.isArray(payload.items)) throw new Error('Work list was incomplete. Please retry.');
    if (page === 1) scope = parseOperatorDeskScope(payload.scope);
    for (const item of payload.items) items.set(item.id, item);
    if (!fetchAll) {
      const limit = input.limit || 200;
      hasMore = payload.items.length >= limit || Boolean(payload.nextPage);
      break;
    }
    if (!payload.nextPage) {
      hasMore = false;
      break;
    }
    if (payload.nextPage <= page) throw new Error('Work list paging failed. Please retry.');
    if (page >= maxPages) {
      hasMore = true;
      break;
    }
    page = payload.nextPage;
  }

  // A converted request is represented by its active haul record on the server.
  return {
    items: [...items.values()].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))),
    scope,
    hasMore,
  };
}

export async function listOperatorDeskWork(input: {
  operator?: string | null;
  api?: string | null;
  limit?: number;
  mine?: boolean;
  history?: boolean;
  /** Private Projects/History: include closed and archived records, not just map-visible work. */
  workspace?: boolean;
  kind?: string | null;
  open?: boolean;
  fetchAll?: boolean;
  maxPages?: number;
}): Promise<OperatorDeskWorkItem[]> {
  return (await listOperatorDeskWorkResult(input)).items;
}

export async function getOperatorDeskWork(id: string): Promise<{
  item: OperatorDeskWorkItem;
  details: string | null;
  isCreator: boolean;
}> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    headers: bidBoardAuthHeaders(),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Work detail failed (${res.status})`);
  }
  return {
    item: payload.item as OperatorDeskWorkItem,
    details: payload.details != null ? String(payload.details) : null,
    isCreator: Boolean(payload.isCreator),
  };
}

export async function quoteOperatorDeskWork(
  id: string,
  input: {
    amount: number;
    proposal: string;
    companyName?: string;
    availableDate?: string;
    estimatedDuration?: string;
    contactName?: string;
    contactPhone?: string;
    autoCallOnAccept?: boolean;
    documents?: Array<{ name: string; mimeType: string; contentBase64: string }>;
  },
): Promise<void> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: bidBoardAuthHeaders(),
    body: JSON.stringify({ action: 'quote', ...input }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Quote failed (${res.status})`);
  }
}

export async function setOperatorDeskArchived(id: string, archived: boolean): Promise<void> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: bidBoardAuthHeaders(),
    body: JSON.stringify({ action: archived ? 'archive' : 'unarchive' }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Could not ${archived ? 'archive' : 'unarchive'} (${res.status})`);
  }
}

export async function saveOperatorDeskQuestions(
  id: string,
  questions: unknown[],
  apiNumber?: string | null,
): Promise<void> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: bidBoardAuthHeaders(),
    body: JSON.stringify({ action: 'questions', questions, apiNumber: apiNumber || null }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Could not save questions (${res.status})`);
  }
}

export async function answerOperatorDeskQuestion(
  id: string,
  questionId: string,
  answer: string,
  extra?: {
    answerNumber?: number | null;
    answerAfter?: number | null;
    answerYes?: boolean | null;
    photoUrl?: string | null;
  },
): Promise<void> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: bidBoardAuthHeaders(),
    body: JSON.stringify({
      action: 'answer',
      questionId,
      answer,
      answerNumber: extra?.answerNumber,
      answerAfter: extra?.answerAfter,
      answerYes: extra?.answerYes,
      photoUrl: extra?.photoUrl || null,
    }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Could not save answer (${res.status})`);
  }
}

export async function pingOperatorDeskLocation(
  id: string,
  input: { lat: number; lng: number; accuracy?: number | null },
): Promise<{ events: string[] }> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: bidBoardAuthHeaders(),
    body: JSON.stringify({
      action: 'location',
      lat: input.lat,
      lng: input.lng,
      accuracy: input.accuracy ?? null,
    }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Location ping failed (${res.status})`);
  }
  return { events: Array.isArray(payload.events) ? payload.events.map(String) : [] };
}

export async function updateOperatorDeskWork(
  id: string,
  input: { note?: string; status?: string; apiNumber?: string | null },
): Promise<void> {
  const res = await fetch(`/api/operator-desk/work/${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: bidBoardAuthHeaders(),
    body: JSON.stringify(input),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok || payload.success === false) {
    throw new Error(payload.error || `Work update failed (${res.status})`);
  }
}

let wellOperatorNames: string[] | null = null;
let wellOperatorLoad: Promise<string[]> | null = null;

export async function listOperators(): Promise<string[]> {
  if (wellOperatorNames) return wellOperatorNames;
  if (!wellOperatorLoad) {
    wellOperatorLoad = (async () => {
      const res = await fetch('/api/operators/list');
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload.success === false) {
        wellOperatorLoad = null;
        return [];
      }
      const names = Array.isArray(payload.operators)
        ? payload.operators.map((v: unknown) => String(v || '').trim()).filter(Boolean)
        : [];
      wellOperatorNames = names;
      return names;
    })();
  }
  return wellOperatorLoad;
}

const PERMIAN = { lat: 32.25, lng: -103.78 };
const GLOBE_FACILITY_LIMIT = 250;

export async function loadOperatorDeskFacilities(operator: string): Promise<Record<string, unknown>[]> {
  const name = operatorDeskBrandKey(operator) || String(operator || '').trim();
  if (!name) return [];
  const params = new URLSearchParams({
    operator: name,
    lat: String(PERMIAN.lat),
    lng: String(PERMIAN.lng),
    radius: '250',
    limit: String(GLOBE_FACILITY_LIMIT),
  });
  const res = await fetch(`/api/facilities/search?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  const rows = (data.facilities || data.results || []) as Record<string, unknown>[];
  return Array.isArray(rows) ? rows.slice(0, GLOBE_FACILITY_LIMIT) : [];
}
